import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import {
  configureBrownGoogleAdsPhoneConversion,
  ensureBrownGoogleAdsTagLoaded,
  ensureBrownGtmLoaded,
  isBrownGtmPath,
} from "@/lib/brownGoogleTagManager";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
const NotFound = lazy(() => import("@/pages/not-found"));
const Home = lazy(() => import("@/pages/home"));
const ContactDetails = lazy(() => import("@/pages/contact-details"));
const Reviews = lazy(() => import("@/pages/reviews"));
const Landing = lazy(() => import("@/pages/landing"));
const Team = lazy(() => import("@/pages/team"));
const SacramentoCashOffer = lazy(() => import("@/pages/sacramento-cash-offer"));
const SacramentoCashOfferContact = lazy(() => import("@/pages/sacramento-cash-offer-contact"));
const Foreclosure = lazy(() => import("@/pages/foreclosure"));
const Probate = lazy(() => import("@/pages/probate"));
const OfferStepTwo = lazy(() => import("@/pages/offer-step-two"));

const queryClient = new QueryClient();
const FORECLOSURE_PATH = "/sell-house-foreclosure-sacramento/";

function LegacyForeclosureRedirect() {
  useEffect(() => {
    window.location.replace(FORECLOSURE_PATH);
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#EDF5F9] px-4 text-center text-[#17324D]">
      <p>
        This page has moved.{" "}
        <a className="font-bold underline" href={FORECLOSURE_PATH}>
          View Sacramento foreclosure options
        </a>
        .
      </p>
    </main>
  );
}

function Router() {
  const [location] = useLocation();
  useEffect(() => {
    const isBrownRoute = isBrownGtmPath(location);
    if (isBrownRoute) {
      ensureBrownGtmLoaded();
      ensureBrownGoogleAdsTagLoaded(location);
    }
    configureBrownGoogleAdsPhoneConversion(location);
  }, [location]);

  return (
    <Suspense
      fallback={
        <div
          className="min-h-screen bg-white"
          role="status"
          aria-label="Loading page"
        />
      }
    >
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/get-offer" component={ContactDetails} />
        <Route path="/step-2" component={OfferStepTwo} />
        <Route path="/step-2/" component={OfferStepTwo} />
        <Route path="/reviews" component={Reviews} />
        <Route path="/lp" component={Landing} />
        <Route path="/team" component={Team} />
        <Route path="/sacramento-cash-offer" component={SacramentoCashOffer} />
        <Route path="/sacramento-cash-offer/" component={SacramentoCashOffer} />
        <Route path="/sacramento-cash-offer/contact-step-2" component={SacramentoCashOfferContact} />
        <Route path="/sacramento-cash-offer/contact-step-2/" component={SacramentoCashOfferContact} />
        <Route path="/sacramento-cash-offer/contact" component={SacramentoCashOfferContact} />
        <Route path="/sacramento-cash-offer/contact/" component={SacramentoCashOfferContact} />
        <Route path="/sell-house-foreclosure-sacramento" component={Foreclosure} />
        <Route path="/sell-house-foreclosure-sacramento/" component={Foreclosure} />
        <Route path="/sell-inherited-probate-property-sacramento" component={Probate} />
        <Route path="/sell-inherited-probate-property-sacramento/" component={Probate} />
        <Route path="/fc" component={LegacyForeclosureRedirect} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <Router />
      </WouterRouter>
    </QueryClientProvider>
  );
}

export default App;
